import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import apiGet, { preBookingsApiGetList } from 'lib/network/apiGet'
import PreBookingStatusBadge from 'pages/preBookings/PreBookingStatusBadge'
import { BookingStatusBadge, PaymentStatusBadge } from 'pages/booking/BookingStatusBadges'
import { ownerLabel } from 'pages/preBookings/preBookingHelpers'

const PAGE_SIZE = 10

const carLabel = rentalCar => [rentalCar?.car?.make?.make, rentalCar?.car?.model?.modal, rentalCar?.modelYear].filter(Boolean).join(' ') || '-'

const FunnelList = ({ title, viewAllHref, fetchPage, columns, rowHref, resetKey }) => {
  const { t } = useTranslation()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const load = async nextPage => {
    setLoading(true)
    try {
      const res = await fetchPage(nextPage)
      setRows(prev => (nextPage === 1 ? res.rows : [...prev, ...res.rows]))
      setTotal(res.total)
      setPage(nextPage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(1)
  }, [resetKey])

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {title} <span className="text-sm font-normal text-gray-500">({total.toLocaleString()})</span>
        </h3>
        <Link to={viewAllHref} className="text-sm text-indigo-600 hover:text-indigo-800">
          {t('statsDashboard.operationFunnel.viewAll')}
        </Link>
      </div>
      {rows.length === 0 && !loading ? (
        <p className="text-sm text-gray-500 text-center py-8">{t('statsDashboard.operationFunnel.noRecords')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b">
                {columns.map(col => (
                  <th key={col.label} className="py-2 px-2 text-left rtl:text-right font-medium whitespace-nowrap">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row._id || row.id} className="h-12 border-b last:border-0 hover:bg-gray-50">
                  {columns.map((col, i) => (
                    <td key={col.label} className="py-2 px-2 text-gray-700 whitespace-nowrap">
                      {i === 0 ? (
                        <Link to={rowHref(row)} className="text-indigo-600 hover:text-indigo-800">
                          {col.render(row)}
                        </Link>
                      ) : (
                        col.render(row)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length < total && (
        <div className="text-center mt-auto pt-4">
          <button type="button" disabled={loading} onClick={() => load(page + 1)} className="text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
            {loading ? t('common.loading') : t('common.loadMore')}
          </button>
        </div>
      )}
    </div>
  )
}

const OperationFunnelLists = ({ startDate, endDate }) => {
  const { t } = useTranslation()
  const from = startDate ? startDate.slice(0, 10) : ''
  const to = endDate ? endDate.slice(0, 10) : ''
  const dateQuery = from && to ? `?sd=${from}&ed=${to}` : ''

  const fetchPreBookings = async page => {
    const params = { pageNumber: page, pageSize: PAGE_SIZE }
    if (from) params.from = from
    if (to) params.to = to
    const res = await preBookingsApiGetList(params)
    return { rows: res?.data?.rows || [], total: res?.data?.total || 0 }
  }

  const fetchBookings = async page => {
    const params = { page, size: PAGE_SIZE, excludeBookingCreatedBy: 'office' }
    if (from) params.sd = from
    if (to) params.ed = to
    const res = await apiGet('bookings', params)
    return { rows: res?.data?.list || [], total: res?.data?.pagination?.totalRecords || 0 }
  }

  return (
    <div className="space-y-6">
      <FunnelList
        title={t('statsDashboard.operationFunnel.preBookingsList')}
        viewAllHref="/pre-bookings"
        fetchPage={fetchPreBookings}
        rowHref={row => `/pre-bookings/${row._id}`}
        resetKey={`${from}|${to}`}
        columns={[
          { label: t('preBookings.columns.car'), render: row => carLabel(row?.carId) },
          { label: t('preBookings.columns.customer'), render: row => ownerLabel(row, t('preBookings.guest')) },
          { label: t('preBookings.columns.status'), render: row => <PreBookingStatusBadge status={row.status} t={t} /> },
          { label: t('preBookings.columns.created'), render: row => (row.createdAt ? moment(row.createdAt).format('DD MMM, HH:mm') : '-') },
        ]}
      />
      <FunnelList
        title={t('statsDashboard.operationFunnel.bookingsList')}
        viewAllHref={`/bookings${dateQuery}`}
        fetchPage={fetchBookings}
        rowHref={row => `/booking/${row._id || row.id}`}
        resetKey={`${from}|${to}`}
        columns={[
          { label: t('bookings.columns.refrenceId'), render: row => row.bookingReferenceId || row._id || row.id },
          { label: t('bookings.columns.car'), render: row => carLabel(row?.rentalCar) },
          { label: t('bookings.columns.user'), render: row => row?.user?.fullName || row?.user?.email || row?.user?.phone || '-' },
          { label: t('bookings.columns.bookingStatus'), render: row => <BookingStatusBadge status={row.status} isOfficeBlock={row.isOfficeBlock} /> },
          { label: t('bookings.columns.paymentStatus'), render: row => <PaymentStatusBadge status={row.paymentStatus} /> },
          { label: t('bookings.columns.bookingDate'), render: row => (row.bookingDate ? moment.parseZone(row.bookingDate).format('DD MMM, HH:mm') : '-') },
        ]}
      />
    </div>
  )
}

export default OperationFunnelLists
